import { useState, useEffect } from 'react'
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
} from '@mui/material'
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Save as SaveIcon,
} from '@mui/icons-material'
import { settingsApi } from '../lib/api'
import { ApiConfig, Quota } from '../types'

export default function Settings() {
  const [quota, setQuota] = useState<Quota | null>(null)
  const [apiKeys, setApiKeys] = useState<ApiConfig[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  // API Key 表单状态
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editingKey, setEditingKey] = useState<ApiConfig | null>(null)
  const [formData, setFormData] = useState({
    platform: 'stepfun' as 'stepfun' | 'openai' | 'custom',
    name: '',
    baseUrl: '',
    apiKey: '',
    model: '',
    isPrimary: false,
  })

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    try {
      const [quotaData, keysData] = await Promise.all([
        settingsApi.getQuota(),
        settingsApi.getApiKeys(),
      ])
      setQuota(quotaData)
      setApiKeys(keysData)
    } catch (err: any) {
      setError(err.message || '加载设置失败')
    } finally {
      setLoading(false)
    }
  }

  const handleOpenDialog = (key?: ApiConfig) => {
    if (key) {
      setEditingKey(key)
      setFormData({
        platform: key.platform,
        name: key.name,
        baseUrl: key.baseUrl,
        apiKey: '', // 不显示已加密的key
        model: key.model,
        isPrimary: key.isPrimary,
      })
    } else {
      setEditingKey(null)
      setFormData({
        platform: 'stepfun',
        name: '',
        baseUrl: 'https://api.stepfun.com/v1',
        apiKey: '',
        model: 'step-3.6',
        isPrimary: false,
      })
    }
    setDialogOpen(true)
  }

  const handleCloseDialog = () => {
    setDialogOpen(false)
    setEditingKey(null)
  }

  const handleSubmit = async () => {
    try {
      if (editingKey) {
        await settingsApi.updateApiKey(editingKey.id, formData)
      } else {
        await settingsApi.createApiKey(formData)
      }
      handleCloseDialog()
      loadData()
    } catch (err: any) {
      setError(err.message || '保存失败')
    }
  }

  const handleDelete = async (id: string) => {
    if (!confirm('确定要删除这个 API Key 吗？')) return
    try {
      await settingsApi.deleteApiKey(id)
      loadData()
    } catch (err: any) {
      setError(err.message || '删除失败')
    }
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        系统设置
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* 配额信息 */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            配额使用情况
          </Typography>
          {quota && (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 3, mt: 2 }}>
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  每日图片额度
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{ flexGrow: 1 }}>
                    <Box
                      sx={{
                        height: 8,
                        bgcolor: 'grey.200',
                        borderRadius: 4,
                        overflow: 'hidden',
                      }}
                    >
                      <Box
                        sx={{
                          height: '100%',
                          bgcolor: quota.dailyImagesUsed >= quota.dailyImagesLimit ? 'error.main' : 'primary.main',
                          width: `${Math.min((quota.dailyImagesUsed / quota.dailyImagesLimit) * 100, 100)}%`,
                          transition: 'width 0.3s',
                        }}
                      />
                    </Box>
                  </Box>
                  <Typography variant="body2" sx={{ minWidth: 100, textAlign: 'right' }}>
                    {quota.dailyImagesUsed} / {quota.dailyImagesLimit}
                  </Typography>
                </Box>
                <Typography variant="caption" color="text.secondary">
                  每日重置: {quota.dailyImagesResetAt ? new Date(quota.dailyImagesResetAt).toLocaleTimeString() : '未设置'}
                </Typography>
              </Box>

              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  每月练习生成
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{ flexGrow: 1 }}>
                    <Box
                      sx={{
                        height: 8,
                        bgcolor: 'grey.200',
                        borderRadius: 4,
                        overflow: 'hidden',
                      }}
                    >
                      <Box
                        sx={{
                          height: '100%',
                          bgcolor: quota.monthlyExercisesUsed >= quota.monthlyExercisesLimit ? 'error.main' : 'secondary.main',
                          width: `${Math.min((quota.monthlyExercisesUsed / quota.monthlyExercisesLimit) * 100, 100)}%`,
                          transition: 'width 0.3s',
                        }}
                      />
                    </Box>
                  </Box>
                  <Typography variant="body2" sx={{ minWidth: 100, textAlign: 'right' }}>
                    {quota.monthlyExercisesUsed} / {quota.monthlyExercisesLimit}
                  </Typography>
                </Box>
                <Typography variant="caption" color="text.secondary">
                  每月重置: {quota.monthlyExercisesResetAt ? new Date(quota.monthlyExercisesResetAt).toLocaleDateString() : '未设置'}
                </Typography>
              </Box>

              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  存储空间
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{ flexGrow: 1 }}>
                    <Box
                      sx={{
                        height: 8,
                        bgcolor: 'grey.200',
                        borderRadius: 4,
                        overflow: 'hidden',
                      }}
                    >
                      <Box
                        sx={{
                          height: '100%',
                          bgcolor: quota.storageUsed >= quota.storageLimit ? 'error.main' : 'warning.main',
                          width: `${Math.min((quota.storageUsed / quota.storageLimit) * 100, 100)}%`,
                          transition: 'width 0.3s',
                        }}
                      />
                    </Box>
                  </Box>
                  <Typography variant="body2" sx={{ minWidth: 100, textAlign: 'right' }}>
                    {quota.storageUsed}MB / {quota.storageLimit}MB
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* API Key 管理 */}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6">API Key 管理</Typography>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>
              添加 Key
            </Button>
          </Box>

          {apiKeys.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
              暂无 API Key，请添加
            </Typography>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>名称</TableCell>
                    <TableCell>平台</TableCell>
                    <TableCell>模型</TableCell>
                    <TableCell>Base URL</TableCell>
                    <TableCell>主用</TableCell>
                    <TableCell>创建时间</TableCell>
                    <TableCell align="right">操作</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {apiKeys.map(key => (
                    <TableRow key={key.id} hover>
                      <TableCell>{key.name}</TableCell>
                      <TableCell>
                        <Chip label={key.platform} size="small" />
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'monospace' }}>{key.model}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                        {key.baseUrl}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={key.isPrimary ? '是' : '否'}
                          size="small"
                          color={key.isPrimary ? 'primary' : 'default'}
                        />
                      </TableCell>
                      <TableCell>{key.createdAt ? new Date(key.createdAt).toLocaleDateString() : '-'}</TableCell>
                      <TableCell align="right">
                        <IconButton size="small" onClick={() => handleOpenDialog(key)}>
                          <EditIcon />
                        </IconButton>
                        <IconButton size="small" color="error" onClick={() => handleDelete(key.id)}>
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* 添加/编辑 Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingKey ? '编辑 API Key' : '添加 API Key'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="名称"
              value={formData.name}
              onChange={e => setFormData({ ...formData, name: e.target.value })}
              required
              placeholder="如：阶跃星辰主账号"
            />
            <FormControl fullWidth>
              <InputLabel>平台</InputLabel>
              <Select
                value={formData.platform}
                label="平台"
                onChange={e => setFormData({ ...formData, platform: e.target.value as any })}
              >
                <MenuItem value="stepfun">阶跃星辰 (StepFun)</MenuItem>
                <MenuItem value="openai">OpenAI 兼容</MenuItem>
                <MenuItem value="custom">自定义</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="API Key"
              value={formData.apiKey}
              onChange={e => setFormData({ ...formData, apiKey: e.target.value })}
              required
              type="password"
              placeholder={editingKey ? '留空则不修改' : '请输入 API Key'}
            />
            <TextField
              label="Base URL"
              value={formData.baseUrl}
              onChange={e => setFormData({ ...formData, baseUrl: e.target.value })}
              required
              placeholder="https://api.stepfun.com/v1"
            />
            <TextField
              label="模型名称"
              value={formData.model}
              onChange={e => setFormData({ ...formData, model: e.target.value })}
              required
              placeholder="step-3.6"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isPrimary}
                  onChange={e => setFormData({ ...formData, isPrimary: e.target.checked })}
                />
              }
              label="设为主用 Key"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>取消</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            startIcon={<SaveIcon />}
            disabled={!formData.name || !formData.apiKey || !formData.baseUrl || !formData.model}
          >
            保存
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
